import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Typography,
} from '@mui/material';

import axios from 'axios';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { url } from 'src/Helpers/utils/Constant';
import { ISingleOwnershipChangeApplication } from 'src/Types/module/applications/applications';
import { getStatusLabel } from 'src/Utils/Combined';
import PdfAndImageView from 'src/components/pdfAndImageView/PdfAndImageView';
import { CommonTypoGraphy } from 'src/components/pdfAndImageView/CommonTypoGraphy';
import UpdateOwnerShipChangeApp from './UpdateOwnerShipChangeApp';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import OfflinePaymentCardInformation from '../../subModules/invoice/singleInvoice/OfflinePaymentCardInformation';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from 'src/components/CommonDetailItem/CommonDetailItem';

const mainLabelWidth = { xs: '130px', sm: '150px', md: '175px' };
const subLabelWidth = { xs: '100px', sm: '110px', md: '120px' };

const SingleOwnershipChangeApplication = () => {
  const [showModal, setShowModal] = useState(false);
  const { id } = useParams();
  const [application, setApplication] =
    useState<ISingleOwnershipChangeApplication | null>(null);
  const navigate = useNavigate();
  const [imageViewOpen, SetImageViewOpen] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  useEffect(() => {
    (async () => {
      try {
        const data = await axios.get(
          `${url}/api/member/owner-change-application/${id}`
        );

        if (data.data.success) {
          setApplication(data.data.data);
        }
      } catch (err) {
        alert('No application found');
        navigate('/update-applications/ownership-change-application');
      }
    })();
  }, []);

  const applicationDate = formatDate(
    application?.ownership_change_application_created_at,
    'MMM Do YYYY'
  );

  const statusLabel = application?.ownership_change_application_status
    ? getStatusLabel(application.ownership_change_application_status)
    : 'N/A';

  const mocatApprovalFile =
    application?.ownership_change_application_mocat_approval_copy_file ? (
      <CommonTypoGraphy
        name='View MOCAT Approval Copy'
        onClick={() => {
          SetImageViewOpen(true);
          setImageFile(
            application.ownership_change_application_mocat_approval_copy_file
          );
        }}
      />
    ) : (
      'N/A'
    );

  const mocatEnglishFile =
    application?.ownership_change_application_mocat_approval_copy_file ? (
      <CommonTypoGraphy
        name='View MOCAT Cert English Copy'
        onClick={() => {
          SetImageViewOpen(true);
          setImageFile(
            application.ownership_change_application_mocat_approval_copy_file
          );
        }}
      />
    ) : (
      'N/A'
    );

  const tradeLicenseFile =
    application?.ownership_change_application_trade_license ? (
      <CommonTypoGraphy
        name='View MOCAT Cert Trade License'
        onClick={() => {
          SetImageViewOpen(true);
          setImageFile(
            application.ownership_change_application_trade_license
          );
        }}
      />
    ) : (
      'N/A'
    );

  const taxReturnFile =
    application?.ownership_change_application_tax_certificate ? (
      <CommonTypoGraphy
        name='Proof of Tax Return'
        onClick={() => {
          SetImageViewOpen(true);
          setImageFile(
            application.ownership_change_application_tax_certificate
          );
        }}
      />
    ) : (
      'N/A'
    );

  const forwardingLetterFile =
    application?.ownership_change_application_forwarding_letter ? (
      <CommonTypoGraphy
        name='View Forwarding Letter'
        onClick={() => {
          SetImageViewOpen(true);
          setImageFile(
            application.ownership_change_application_forwarding_letter
          );
        }}
      />
    ) : (
      'N/A'
    );

  const deedFile =
    application?.ownership_change_application_ownership_change_deed ? (
      <CommonTypoGraphy
        name='View Ownership Change Deed'
        onClick={() => {
          SetImageViewOpen(true);
          setImageFile(
            application.ownership_change_application_ownership_change_deed
          );
        }}
      />
    ) : (
      'N/A'
    );

  // Prev Owner files
  const prevNidFile = application?.prev_company_owner_nid_file ? (
    <CommonTypoGraphy
      name='View NID'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.prev_company_owner_nid_file);
      }}
    />
  ) : (
    'N/A'
  );

  const prevPhotoFile = application?.prev_company_owner_photo ? (
    <CommonTypoGraphy
      name='View Photo'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.prev_company_owner_photo);
      }}
    />
  ) : (
    'N/A'
  );

  const prevBiodataFile = application?.prev_company_owner_biodata ? (
    <CommonTypoGraphy
      name='View Biodata'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.prev_company_owner_biodata);
      }}
    />
  ) : (
    'N/A'
  );

  const prevPassportFile = application?.prev_company_owner_passport ? (
    <CommonTypoGraphy
      name='View Passport'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.prev_company_owner_passport);
      }}
    />
  ) : (
    'N/A'
  );

  const prevSigFile = application?.prev_company_owner_signature ? (
    <CommonTypoGraphy
      name='View Signature'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.prev_company_owner_signature);
      }}
    />
  ) : (
    'N/A'
  );

  // New Owner files
  const newNidFile = application?.company_owner_nid_file ? (
    <CommonTypoGraphy
      name='View NID'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.company_owner_nid_file);
      }}
    />
  ) : (
    'N/A'
  );

  const newPhotoFile = application?.company_owner_photo ? (
    <CommonTypoGraphy
      name='View Photo'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.company_owner_photo);
      }}
    />
  ) : (
    'N/A'
  );

  const newBiodataFile = application?.company_owner_biodata ? (
    <CommonTypoGraphy
      name='View Biodata'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.company_owner_biodata);
      }}
    />
  ) : (
    'N/A'
  );

  const newPassportFile = application?.company_owner_passport ? (
    <CommonTypoGraphy
      name='View Passport'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.company_owner_passport);
      }}
    />
  ) : (
    'N/A'
  );

  const newSigFile = application?.company_owner_signature ? (
    <CommonTypoGraphy
      name='View Signature'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.company_owner_signature);
      }}
    />
  ) : (
    'N/A'
  );

  return (
    <>
      <Helmet>
        <title>Atab - Ownership change application</title>
      </Helmet>
      <Card sx={{ mx: 2 }}>
        <CardHeader
          title='Ownership change application'
          action={
            <Button onClick={() => setShowModal(true)} variant='outlined'>
              Update
            </Button>
          }
        />
        <Divider />
        <CardContent>
          {application ? (
            <Grid container spacing={2} pt={2}>
              <CommonDetailItem
                label='Application Date'
                value={applicationDate}
                labelWidth={mainLabelWidth}
                xs={12}
                sm={6}
                md={4}
              />
              <CommonDetailItem
                label='Company name'
                value={formatValue(application.user_member_company_name)}
                labelWidth={mainLabelWidth}
                xs={12}
                sm={6}
                md={4}
              />
              <CommonDetailItem
                label='Status'
                value={statusLabel}
                labelWidth={mainLabelWidth}
                xs={12}
                sm={6}
                md={4}
              />
              <CommonDetailItem
                label='MOCAT Approval'
                value={mocatApprovalFile}
                labelWidth={mainLabelWidth}
                xs={12}
                sm={6}
                md={4}
              />
              <CommonDetailItem
                label='MOCAT Cert English'
                value={mocatEnglishFile}
                labelWidth={mainLabelWidth}
                xs={12}
                sm={6}
                md={4}
              />
              <CommonDetailItem
                label='Trade License'
                value={tradeLicenseFile}
                labelWidth={mainLabelWidth}
                xs={12}
                sm={6}
                md={4}
              />
              <CommonDetailItem
                label='Tax Return Receipt'
                value={taxReturnFile}
                labelWidth={mainLabelWidth}
                xs={12}
                sm={6}
                md={4}
              />
              <CommonDetailItem
                label='Forwarding Letter'
                value={forwardingLetterFile}
                labelWidth={mainLabelWidth}
                xs={12}
                sm={6}
                md={4}
              />
              <CommonDetailItem
                label='Ownership Deed'
                value={deedFile}
                labelWidth={mainLabelWidth}
                xs={12}
                sm={6}
                md={4}
              />
              <CommonDetailItem
                label='Note / Reject Reason'
                value={formatValue(
                  application.ownership_change_application_rejected_reason
                )}
                labelWidth={mainLabelWidth}
                xs={12}
                sm={12}
                md={12}
              />

              {/* Previous Owner Information Card */}
              <Grid item xs={12} md={6}>
                <Card variant='outlined'>
                  <CardHeader title='Previous owner information' />
                  <CardContent>
                    <Grid container spacing={2}>
                      <CommonDetailItem
                        label='Name'
                        value={formatValue(
                          application.prev_company_owner_name
                        )}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={12}
                        md={12}
                      />
                      <CommonDetailItem
                        label='Phone'
                        value={formatValue(
                          application.prev_company_owner_phone
                        )}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Designation'
                        value={formatValue(
                          application.prev_company_owner_designation
                        )}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Email'
                        value={formatValue(
                          application.prev_company_owner_email
                        )}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={12}
                        md={12}
                      />
                      <CommonDetailItem
                        label='Address'
                        value={formatValue(
                          application.prev_company_owner_address
                        )}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={12}
                        md={12}
                      />
                      <CommonDetailItem
                        label='NID'
                        value={formatValue(application.prev_company_owner_nid)}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='NID File'
                        value={prevNidFile}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Owner Photo'
                        value={prevPhotoFile}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Biodata'
                        value={prevBiodataFile}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Passport'
                        value={prevPassportFile}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Signature'
                        value={prevSigFile}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* New Owner Information Card */}
              <Grid item xs={12} md={6}>
                <Card variant='outlined'>
                  <CardHeader title='New owner information' />
                  <CardContent>
                    <Grid container spacing={2}>
                      <CommonDetailItem
                        label='Name'
                        value={formatValue(application.company_owner_name)}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={12}
                        md={12}
                      />
                      <CommonDetailItem
                        label='Phone'
                        value={formatValue(application.company_owner_phone)}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Designation'
                        value={formatValue(
                          application.company_owner_designation
                        )}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Email'
                        value={formatValue(application.company_owner_email)}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={12}
                        md={12}
                      />
                      <CommonDetailItem
                        label='Address'
                        value={formatValue(application.company_owner_address)}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={12}
                        md={12}
                      />
                      <CommonDetailItem
                        label='NID'
                        value={formatValue(application.company_owner_nid)}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='NID File'
                        value={newNidFile}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Owner Photo'
                        value={newPhotoFile}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Biodata'
                        value={newBiodataFile}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Passport'
                        value={newPassportFile}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                      <CommonDetailItem
                        label='Signature'
                        value={newSigFile}
                        labelWidth={subLabelWidth}
                        xs={12}
                        sm={6}
                        md={6}
                      />
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Typography
              variant='body1'
              fontWeight='bold'
              color='text.primary'
              display='inline'
              sx={{ pr: 1 }}
              gutterBottom
            >
              No Data found
            </Typography>
          )}
        </CardContent>

        {application?.paymentInfo && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Divider
              sx={{
                position: 'relative',
                justifyContent: 'space-between',
              }}
            >
              <Chip label='Payment Information' size='small' color='primary' />
              <Link
                to={`/invoices/transactions/${application?.paymentInfo?.invoice_id}`}
              >
                <Chip
                  label='Go Invoice'
                  size='small'
                  sx={{
                    position: 'absolute',
                    right: 0,
                    background: '#D7E9FD',
                  }}
                />
              </Link>
            </Divider>

            <OfflinePaymentCardInformation data={application?.paymentInfo} />
          </Grid>
        )}
      </Card>

      {application && (
        <ModalsWrapper
          setShowModal={setShowModal}
          showModal={showModal}
          modalData={{
            title: 'Update OwnerShip Application',
          }}
        >
          <Box px={2}>
            <UpdateOwnerShipChangeApp
              application={application}
              setApplication={setApplication}
              setShowModal={setShowModal}
            />
          </Box>
        </ModalsWrapper>
      )}

      <PdfAndImageView
        setOpen={SetImageViewOpen}
        open={imageViewOpen}
        file={imageFile}
      />
    </>
  );
};

export default SingleOwnershipChangeApplication;
